/* ==========================================================================
   SkinTalk.mx - Product Info Custom Element
   Variant selection state management.
   Updates price, availability, media on variant change.
   ========================================================================== */

if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
    }

    connectedCallback() {
      /* Listen for variant changes from the variant-picker */
      this.addEventListener('variant:changed', this.onVariantChanged.bind(this));
      this.addEventListener('variant:unavailable', this.onVariantUnavailable.bind(this));

      /* Also listen on document for cross-component communication */
      document.addEventListener('variant:changed', (e) => {
        if (e.detail?.sectionId === this.sectionId) {
          this.onVariantChanged(e);
        }
      });
    }

    /**
     * Handle variant changed event - update all product info displays.
     * @param {CustomEvent} event - The variant:changed event.
     */
    onVariantChanged(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      this.updatePrice(variant);
      this.updateSKU(variant);
      this.updateAvailability(variant);
      this.updateMedia(variant);
    }

    /**
     * Handle variant unavailable - no matching variant exists.
     */
    onVariantUnavailable() {
      /* Disable add to cart */
      const addBtn = this.querySelector('[data-add-to-cart]');
      if (addBtn) {
        addBtn.disabled = true;
        const text = addBtn.querySelector('[data-add-to-cart-text]');
        if (text) text.textContent = 'No Disponible';
      }

      /* Update price to show "No disponible" */
      const priceContainer = this.querySelector('[data-product-price]');
      if (priceContainer) {
        priceContainer.innerHTML = '<span class="price--regular" style="color: var(--color-text-muted);">No disponible</span>';
      }
    }

    /**
     * Update the displayed price.
     * @param {Object} variant - The variant object.
     */
    updatePrice(variant) {
      const priceContainer = this.querySelector('[data-product-price]');
      if (!priceContainer) return;

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      let priceHTML = '';

      if (onSale) {
        const savePercent = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        priceHTML = `
          <span class="price--compare" aria-label="Precio original">
            <s>${SkinTalk.formatMoney(variant.compare_at_price)}</s>
          </span>
          <span class="price--sale" aria-label="Precio de oferta">
            ${SkinTalk.formatMoney(variant.price)}
          </span>
          <span class="price__badge price__badge--sale" aria-label="${savePercent}% de descuento">
            -${savePercent}%
          </span>
        `;
        priceContainer.classList.add('price--on-sale');
      } else {
        priceHTML = `
          <span class="price--regular" aria-label="Precio">
            ${SkinTalk.formatMoney(variant.price)}
          </span>
        `;
        priceContainer.classList.remove('price--on-sale');
      }

      priceContainer.innerHTML = priceHTML;

      /* Also update any unit price display */
      const unitPrice = this.querySelector('[data-unit-price]');
      if (unitPrice) {
        if (variant.unit_price) {
          unitPrice.innerHTML = `${SkinTalk.formatMoney(variant.unit_price)} / ${variant.unit_price_measurement.reference_unit}`;
          unitPrice.hidden = false;
        } else {
          unitPrice.hidden = true;
        }
      }
    }

    /**
     * Update the SKU display.
     * @param {Object} variant - The variant object.
     */
    updateSKU(variant) {
      const skuEl = this.querySelector('[data-product-sku]');
      if (!skuEl) return;

      if (variant.sku) {
        skuEl.textContent = variant.sku;
        skuEl.closest('[data-sku-wrapper]')?.removeAttribute('hidden');
      } else {
        skuEl.closest('[data-sku-wrapper]')?.setAttribute('hidden', '');
      }
    }

    /**
     * Update availability indicator.
     * @param {Object} variant - The variant object.
     */
    updateAvailability(variant) {
      const indicator = this.querySelector('[data-availability]');
      if (!indicator) return;

      if (variant.available) {
        indicator.innerHTML = `
          <span class="product-info__availability product-info__availability--in-stock">
            <span class="product-info__availability-dot"></span>
            En Stock
          </span>
        `;
      } else {
        indicator.innerHTML = `
          <span class="product-info__availability product-info__availability--out-of-stock">
            <span class="product-info__availability-dot"></span>
            Agotado
          </span>
        `;
      }
    }

    /**
     * Update media to show variant's featured image.
     * @param {Object} variant - The variant object.
     */
    updateMedia(variant) {
      if (!variant.featured_media) return;

      document.dispatchEvent(new CustomEvent('variant:media-change', {
        detail: { mediaId: variant.featured_media.id }
      }));
    }
  }

  customElements.define('product-info', ProductInfo);
}
